import { ElementType } from "react";
import {
  BriefcaseBusinessIcon,
  BuildingIcon,
  ClockIcon,
  KanbanIcon,
  Share2Icon,
  SparklesIcon,
  UserCogIcon,
  UsersIcon,
} from "lucide-react";

import { CandidacyOwnerCell } from "@/components/CandidacyOwnerCell";
import { CandidacySourceBadge } from "@/components/CandidacySourceBadge";
import { CandidacyStatusBadge } from "@/components/CandidacyStatusBadge";
import { Column } from "@/components/Column";
import { EmptyDataDash } from "@/components/EmptyDataDash";
import { EmptyState } from "@/components/EmptyState";
import { Row } from "@/components/Row";
import { Text } from "@/components/Text";
import { TimeAgoInTableCell } from "@/components/TimeAgoInTableCell";
import { AgenticStaffingStatus } from "@jobs/AgenticStaffingStatus";
import { AgenticScoreChip } from "@jobs/pipeline/shared/AgenticScoreChip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { agenticScoreToBucket } from "@/lib/agenticScore";
import { getScreenFromPathname, mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { cn } from "@/lib/utils";
import { GetOtherCandidaciesResponse } from "@/types";

export const OtherCandidaciesTable = ({
  isDashboardStyle = false,
  otherCandidacies,
}: {
  isDashboardStyle?: boolean;
  otherCandidacies?: GetOtherCandidaciesResponse[];
}) => {
  const getHeader = (Icon: ElementType, label: string, className?: string) => {
    if (isDashboardStyle) {
      return (
        <TableHead className={cn("h-12 px-3", className)}>
          <Text className="whitespace-nowrap text-xs font-medium text-slate-500">
            {label}
          </Text>
        </TableHead>
      );
    }

    return (
      <TableHead className={className}>
        <Row className="items-center gap-2">
          <Icon className="h-4 w-4" />
          <Text variant="header">{label}</Text>
        </Row>
      </TableHead>
    );
  };

  const handleRowClick = (candidacy: GetOtherCandidaciesResponse) => {
    mixpanelUtils.trackRowClick(
      "Other candidacy",
      getScreenFromPathname(location.pathname),
    );
    window.open(
      `/companies/${candidacy.company.id}/jobs/${candidacy.job.id}?c=${candidacy.id}`,
      "_blank",
    );
  };

  /* Render */
  const render = () => {
    return (
      <div
        className={cn(
          "rounded-lg border border-slate-200 bg-white",
          isDashboardStyle ? "min-w-0 overflow-x-auto" : "min-w-[675px]",
        )}
      >
        <Table className={cn(isDashboardStyle && "min-w-[900px] text-sm")}>
          <TableHeader
            className={cn(
              isDashboardStyle &&
                "bg-slate-50 [&_th]:border-b [&_th]:border-slate-200",
            )}
          >
            <TableRow>
              {isDashboardStyle
                ? getHeader(BriefcaseBusinessIcon, "Jobs", "min-w-[220px]")
                : getHeader(BuildingIcon, "Company")}
              {isDashboardStyle
                ? null
                : getHeader(BriefcaseBusinessIcon, "Job")}
              {getHeader(ClockIcon, "Last Updated")}
              {isDashboardStyle
                ? null
                : getHeader(KanbanIcon, "Agentic Staffing Status")}
              {getHeader(KanbanIcon, "Stage", "min-w-[130px]")}
              {getHeader(UsersIcon, "Owner")}
              {getHeader(SparklesIcon, "Agent Score")}
              {getHeader(Share2Icon, "Candidacy Source")}
            </TableRow>
          </TableHeader>
          <TableBody
            className={cn(
              isDashboardStyle &&
                "[&_td]:!px-3 [&_td]:!py-3 [&_tr:last-child_td]:border-b-0 [&_tr_td]:border-b [&_tr_td]:border-slate-100",
            )}
          >
            {otherCandidacies?.length ? (
              otherCandidacies.map((candidacy) => {
                const status = candidacy.lastCandidacyStatusChange.toStatus;
                const fromStatus =
                  candidacy.lastCandidacyStatusChange.fromStatus;
                const bucket = agenticScoreToBucket(
                  candidacy.candidacyAgenticScoreChanges[0]?.toScore,
                );
                return (
                  <TableRow
                    key={candidacy.id}
                    onClick={() => {
                      handleRowClick(candidacy);
                    }}
                  >
                    {isDashboardStyle ? (
                      <TableCell>
                        <Column className="min-w-0 gap-0.5">
                          <Text className="truncate font-medium text-slate-900">
                            {candidacy.job.title}
                          </Text>
                          <Text className="truncate text-xs font-normal text-slate-500">
                            {candidacy.company.name}
                          </Text>
                        </Column>
                      </TableCell>
                    ) : (
                      <>
                        <TableCell>{candidacy.company.name}</TableCell>
                        <TableCell>{candidacy.job.title}</TableCell>
                      </>
                    )}
                    <TableCell>
                      <TimeAgoInTableCell
                        timestamp={candidacy.lastActivityAt}
                      />
                    </TableCell>
                    {isDashboardStyle ? null : (
                      <TableCell>
                        <AgenticStaffingStatus
                          textAIOutreachWithProxy={
                            candidacy.textAIOutreachWithProxy
                          }
                          voiceAIOutreachSlim={candidacy.voiceAIOutreachSlim}
                        />
                      </TableCell>
                    )}
                    <TableCell className="min-w-[130px] whitespace-nowrap">
                      <CandidacyStatusBadge
                        byAgent={candidacy.lastCandidacyStatusChange.byAgent}
                        className="whitespace-nowrap [&>div]:shrink-0 [&>p]:whitespace-nowrap"
                        fromStatus={fromStatus}
                        rejectedReason={
                          candidacy.lastCandidacyStatusChange.statusChangeReason
                        }
                        status={status}
                      />
                    </TableCell>
                    <TableCell>
                      <CandidacyOwnerCell
                        candidacyId={candidacy.id}
                        owners={candidacy.candidacyOwners}
                        readOnly
                      />
                    </TableCell>
                    <TableCell>
                      {bucket ? (
                        <AgenticScoreChip
                          bucket={bucket}
                          isHumanRated={
                            candidacy.candidacyAgenticScoreChanges[0]?.userId !=
                            null
                          }
                          justification={
                            candidacy.candidacyAgenticScoreChanges[0]
                              ?.justification
                          }
                        />
                      ) : (
                        <EmptyDataDash />
                      )}
                    </TableCell>
                    <TableCell>
                      <CandidacySourceBadge
                        createdAt={candidacy.createdAt}
                        jobAndLocation={candidacy.atsJobTitleAndLocation}
                        source={candidacy.candidacySource}
                      />
                    </TableCell>
                  </TableRow>
                  //</Link>
                );
              })
            ) : (
              <TableRow>
                <TableCell className="p-0" colSpan={isDashboardStyle ? 6 : 8}>
                  <EmptyState
                    className="py-4"
                    description="Other jobs this candidate has applied to will appear here."
                    headerClassName="text-xl"
                    Icon={UserCogIcon}
                    title="No other candidacies"
                  />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    );
  };

  return render();
};
